import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { IconUpload, IconFileText } from '@tabler/icons-react'
import classNames from 'classnames'
import { isEmpty, isNil, size } from 'lodash'

import { BasicModal } from '@/components/layout/modals/BasicModal'
import { Button } from '@/components/buttons/Button'
import { Banner } from '@/components/banners/Banner'
import { Loader } from '@/components/loaders/Loader'
import { importOverrides } from '@/reducers/voiceAgents/voiceAgents.actions'

// Trim long values so the diff stays scannable. The full YAML is still
// downloadable on the source env if anyone needs the verbatim text.
const snippet = (value) => {
  if (isNil(value)) return '∅'
  const s = String(value)
  if (s.length <= 60) return JSON.stringify(s)
  return JSON.stringify(`${s.slice(0, 60)}…`)
}

const countChanges = (diff) => {
  if (!diff) return 0
  const blockCount = (b) => size(b?.added) + size(b?.changed) + size(b?.removed)
  return blockCount(diff.global)
}

// Modal that drives the import side of cross-env override promotion (AVR-392).
//
// Lifecycle:
//   1. operator drops a YAML (or picks via file input) → we read it client-side
//   2. dry-run call to BE → returns compatibility + diff + target_hash_before
//   3. render diff preview + any compatibility / drift blockers
//   4. operator clicks Apply → second call, dry_run:false, expected_target_hash
//      threaded from step 2 so we refuse the apply if prod was edited in between
//
// The whole modal resets state on close so the operator can drop a different
// file without stale diff carry-over.
const ImportOverridesModal = ({ show, onClose }) => {
  const dispatch = useDispatch()
  const agentId = useSelector((state) => state.voiceAgents.selectedAgentId)
  const dryRunLoading = useSelector((state) => state.voiceAgents.loading?.importOverridesDryRun)
  const applyLoading = useSelector((state) => state.voiceAgents.loading?.importOverridesApply)

  const [yamlText, setYamlText] = useState('')
  const [fileName, setFileName] = useState('')
  const [dryRunResult, setDryRunResult] = useState(null)
  const [isDragging, setIsDragging] = useState(false)
  const [appliedSummary, setAppliedSummary] = useState(null)
  // Drift error from the apply step. Surfaced inline so the operator
  // re-runs dry-run before retrying, rather than relying on the toast alone.
  const [applyError, setApplyError] = useState(null)
  const fileInputRef = useRef(null)

  // Reset every piece of local state when the modal opens/closes, so we don't
  // surface a stale diff from a previous import session.
  useEffect(() => {
    if (!show) {
      setYamlText('')
      setFileName('')
      setDryRunResult(null)
      setIsDragging(false)
      setAppliedSummary(null)
      setApplyError(null)
    }
  }, [show])

  const handleFile = useCallback(
    async (file) => {
      if (!file) return
      if (!/\.ya?ml$/i.test(file.name)) {
        setDryRunResult({
          ok: false,
          error: 'Only .yaml / .yml files are accepted.',
        })
        return
      }
      const text = await file.text()
      setYamlText(text)
      setFileName(file.name)
      setAppliedSummary(null)
      setApplyError(null)
      const result = await dispatch(importOverrides(agentId, text, { dryRun: true }))
      setDryRunResult(result)
    },
    [dispatch, agentId]
  )

  const onDrop = useCallback(
    (event) => {
      event.preventDefault()
      setIsDragging(false)
      const file = event.dataTransfer?.files?.[0]
      handleFile(file)
    },
    [handleFile]
  )

  const onDropzoneKeyDown = useCallback((event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      fileInputRef.current?.click()
    }
  }, [])

  // Derived client-side rather than read from the BE response — the diff is
  // the single source of truth for whether there is anything to apply.
  // (Compat failures need no separate gate: the BE mirrors compatibility.ok
  // into response.ok and returns an empty diff alongside it.)
  const totalChanges = countChanges(dryRunResult?.diff)
  const hasChanges = totalChanges > 0

  const onApply = useCallback(async () => {
    if (!yamlText || !dryRunResult?.ok || !hasChanges) {
      return
    }
    setApplyError(null)
    const result = await dispatch(
      importOverrides(agentId, yamlText, {
        dryRun: false,
        expectedTargetHash: dryRunResult.target_hash_before,
      })
    )
    if (result?.applied) {
      setAppliedSummary(result)
      setDryRunResult(result)
      return
    }
    // Apply failed — show inline so the operator can decide to re-run dry-run.
    // The thunk normalizes BE errors into { ok:false, error }.
    if (result?.error) {
      setApplyError(result.error)
    }
  }, [dispatch, agentId, yamlText, dryRunResult, hasChanges])

  const renderDropZone = () => (
    <div
      role="button"
      tabIndex={0}
      className={classNames('voice-agents__import-dropzone', { 'is-dragging': isDragging })}
      onDragOver={(event) => {
        event.preventDefault()
        setIsDragging(true)
      }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={onDrop}
      onClick={() => fileInputRef.current?.click()}
      onKeyDown={onDropzoneKeyDown}
      data-testid="import-dropzone"
    >
      <IconUpload size={32} />
      <p>Drop overrides YAML here</p>
      <p className="voice-agents__import-dropzone-sub">or click to browse</p>
      <input
        type="file"
        accept=".yaml,.yml,application/x-yaml,text/yaml"
        ref={fileInputRef}
        className="voice-agents__import-fileinput"
        onChange={(event) => handleFile(event.target.files?.[0])}
      />
    </div>
  )

  const renderDiff = () => {
    // Post-apply the "Applied" banner below is the whole story — don't
    // re-render the just-applied diff as if it were still pending.
    if (appliedSummary) return null
    if (!dryRunResult) return null
    if (dryRunResult.error) {
      return (
        <Banner critical header="Import failed" data-testid="import-error-banner">
          <p>{dryRunResult.error}</p>
        </Banner>
      )
    }
    const { compatibility, diff } = dryRunResult

    if (compatibility && !compatibility.ok) {
      return (
        <Banner
          critical
          header="Structural mismatch — apply blocked"
          data-testid="import-compat-blocked"
        >
          <CompatList compat={compatibility} />
        </Banner>
      )
    }
    if (!hasChanges) {
      return (
        <Banner success header="Up to date" data-testid="import-uptodate">
          <p>This environment already matches the file.</p>
        </Banner>
      )
    }
    return (
      <div className="voice-agents__import-diff" data-testid="import-diff">
        <DiffBlock title="Global" block={diff?.global} />
      </div>
    )
  }

  return (
    <BasicModal
      title={agentId ? `Import overrides — ${agentId}` : 'Import overrides'}
      show={show}
      onClose={onClose}
      size="large"
      scrolling
      footer={
        <div className="voice-agents__import-modal-footer">
          <Button onClick={onClose} data-testid="import-cancel">
            {appliedSummary ? 'Close' : 'Cancel'}
          </Button>
          <Button
            primary
            disabled={!dryRunResult?.ok || !hasChanges || applyLoading || Boolean(appliedSummary)}
            loading={applyLoading}
            onClick={onApply}
            data-testid="import-apply"
          >
            {appliedSummary
              ? 'Applied'
              : totalChanges > 0
                ? `Apply ${totalChanges} change${totalChanges === 1 ? '' : 's'}`
                : 'Apply'}
          </Button>
        </div>
      }
    >
      {!yamlText && renderDropZone()}
      {yamlText && (
        <div className="voice-agents__import-filechip" data-testid="import-filechip">
          <IconFileText size={16} />
          <span>{fileName}</span>
          <Button
            basic
            size="mini"
            onClick={() => {
              setYamlText('')
              setFileName('')
              setDryRunResult(null)
              setAppliedSummary(null)
              setApplyError(null)
            }}
            disabled={applyLoading || dryRunLoading}
          >
            Replace
          </Button>
        </div>
      )}
      {dryRunLoading && (
        <div className="voice-agents__import-loading" data-testid="import-dryrun-loading">
          <Loader size="small" />
          <span>Analyzing…</span>
        </div>
      )}
      {!dryRunLoading && renderDiff()}
      {applyError && (
        <Banner
          critical
          header="Apply failed — target may have changed"
          data-testid="import-apply-error"
        >
          <p>{applyError}</p>
          <p>
            The target environment may have been edited since the dry-run. Click{' '}
            <strong>Replace</strong> above and re-drop the file to refresh the diff before retrying.
          </p>
        </Banner>
      )}
      {appliedSummary && (
        <Banner success header="Applied" data-testid="import-applied-banner">
          <p>Config version bumped to {appliedSummary.applied?.config_version_after}.</p>
        </Banner>
      )}
    </BasicModal>
  )
}

const CompatList = ({ compat }) => {
  if (!compat) return null
  // Non-provisioned agents come back as a 200 with agent_provisioned:false and
  // empty missing_* arrays — without this branch the banner body would be blank.
  if (!compat.agent_provisioned) {
    return <p>Agent is not provisioned on this env. Run setup first.</p>
  }
  const sections = [
    ['Variables not on this env', compat.missing_variables],
    ['Nodes not on this env', compat.missing_nodes],
    ['Tools not on this env', compat.missing_tools],
  ].filter(([, items]) => !isEmpty(items))

  if (isEmpty(sections)) {
    return null
  }
  return (
    <ul>
      {sections.map(([label, items]) => (
        <li key={label}>
          <strong>{label}:</strong> {items.join(', ')}
        </li>
      ))}
    </ul>
  )
}

const DiffBlock = ({ title, block }) => {
  if (!block) return null
  const { added = [], changed = [], removed = [] } = block
  if (isEmpty(added) && isEmpty(changed) && isEmpty(removed)) return null
  return (
    <div className="voice-agents__import-diffblock">
      <h4>{title}</h4>
      {added.map((entry) => (
        <div key={`+${entry.kind}-${entry.name}`} className="voice-agents__diff-add">
          +{' '}
          <strong>
            {entry.kind}.{entry.name}
          </strong>
          : {snippet(entry.new)}
        </div>
      ))}
      {changed.map((entry) => (
        <div key={`~${entry.kind}-${entry.name}`} className="voice-agents__diff-change">
          ~{' '}
          <strong>
            {entry.kind}.{entry.name}
          </strong>
          : {snippet(entry.old)} → {snippet(entry.new)}
        </div>
      ))}
      {removed.map((entry) => (
        <div key={`-${entry.kind}-${entry.name}`} className="voice-agents__diff-remove">
          −{' '}
          <strong>
            {entry.kind}.{entry.name}
          </strong>{' '}
          (removed)
        </div>
      ))}
    </div>
  )
}

export default ImportOverridesModal
